import type { UseDeleteOptions } from "./useDelete.type";
import type { ApiError } from "@/services/api";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { client } from "@/config/client";

export const useDelete = <Variables = unknown>(
  model: string,
  id: number,
  options?: UseDeleteOptions<Variables>
) => {
  const queryClient = useQueryClient();

  const {
    rootId,
    invalidateKeys,
    onSuccess,
    predicateRemoveKeys,
    mutationOptions,
  } = options ?? {};

  const mutationFn = () => {
    if (rootId) {
      return client[model].destroy(rootId, id);
    }
    return client[model].destroy(id);
  };

  const onDeleteSuccess = async () => {
    onSuccess?.();

    await queryClient.cancelQueries();

    queryClient.removeQueries({
      predicate: ({ queryKey }) => {
        return queryKey.includes(id) && queryKey.includes(model);
      },
    });

    if (predicateRemoveKeys) {
      queryClient.removeQueries({ predicate: predicateRemoveKeys });
    }

    queryClient.invalidateQueries({ queryKey: [model] });
    invalidateKeys?.forEach((queryKey) =>
      queryClient.invalidateQueries({ queryKey })
    );
  };

  return useMutation<null, ApiError, Variables>({
    ...mutationOptions,
    mutationFn,
    onSuccess: onDeleteSuccess,
  });
};
